import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Edit3,
  Save,
  X,
  Info,
  TrendingUp,
} from 'lucide-react';

const STORAGE_KEY = 'ot_assistant_area4_budget_v1';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Defaults seeded from the Tableau dashboard snapshot the user shared
// (YTD figures for 214-0000 Deputy Chief — Area 4, May 2026).
const DEFAULT_SETTINGS = {
  unitLabel: 'Area 4 (Unit 214)',
  annualBudget: 694083,
  annualSpend: 232933,
  monthlyBudget: 57840,
  monthlySpend: 0,
  supervisorReserveMonthly: 6500,
  selectedMonth: new Date().getMonth(),
  asOfDate: new Date().toISOString().slice(0, 10),
};

const WARN_THRESHOLD = 0.85;

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch (e) {
    return DEFAULT_SETTINGS;
  }
}

function saveSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    // ignore quota errors
  }
}

function computeStatus(spend, reserve, budget) {
  if (!budget || budget <= 0) {
    return { level: 'gray', label: 'Not Set', pct: 0 };
  }
  const total = (spend || 0) + (reserve || 0);
  const pct = (total / budget) * 100;
  if (total >= budget) return { level: 'red', label: 'Over Budget', pct };
  if (total >= WARN_THRESHOLD * budget) return { level: 'yellow', label: 'Approaching Limit', pct };
  return { level: 'green', label: 'Funds Available', pct };
}

const STATUS_STYLES = {
  green: {
    tileBg: 'bg-emerald-50',
    border: 'border-emerald-300',
    pill: 'bg-emerald-600 text-white',
    bar: 'bg-emerald-500',
    text: 'text-emerald-800',
    icon: CheckCircle,
  },
  yellow: {
    tileBg: 'bg-amber-50',
    border: 'border-amber-300',
    pill: 'bg-amber-500 text-white',
    bar: 'bg-amber-500',
    text: 'text-amber-800',
    icon: AlertTriangle,
  },
  red: {
    tileBg: 'bg-red-50',
    border: 'border-red-300',
    pill: 'bg-red-600 text-white',
    bar: 'bg-red-500',
    text: 'text-red-800',
    icon: AlertCircle,
  },
  gray: {
    tileBg: 'bg-slate-50',
    border: 'border-slate-300',
    pill: 'bg-slate-400 text-white',
    bar: 'bg-slate-400',
    text: 'text-slate-600',
    icon: Info,
  },
};

function fmtUSD(n) {
  const v = Math.round(n || 0);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US')}`;
}

function parseAsOf(raw) {
  if (!raw) return new Date();
  // Treat YYYY-MM-DD as a local date (avoid UTC shift)
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(raw);
  return isNaN(d.getTime()) ? new Date() : d;
}

function getElapsed(asOfDate, selectedMonth) {
  const asOf = parseAsOf(asOfDate);
  const year = asOf.getFullYear();

  // Monthly: how far through the selected month are we?
  const monthIdx = Number.isInteger(selectedMonth) ? selectedMonth : asOf.getMonth();
  const totalDaysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  let monthDaysElapsed;
  const inSelectedMonth = asOf.getFullYear() === year && asOf.getMonth() === monthIdx;
  if (inSelectedMonth) {
    monthDaysElapsed = asOf.getDate();
  } else if (
    asOf.getFullYear() > year ||
    (asOf.getFullYear() === year && asOf.getMonth() > monthIdx)
  ) {
    monthDaysElapsed = totalDaysInMonth; // month is fully in the past
  } else {
    monthDaysElapsed = 0; // month hasn't started yet
  }
  const monthFraction = totalDaysInMonth > 0 ? monthDaysElapsed / totalDaysInMonth : 0;

  // Annual: how far through the calendar year are we?
  const yearStart = new Date(year, 0, 1);
  const nextYearStart = new Date(year + 1, 0, 1);
  const totalDaysInYear = Math.round((nextYearStart - yearStart) / 86400000);
  const yearDaysElapsed = Math.min(
    totalDaysInYear,
    Math.max(0, Math.round((asOf - yearStart) / 86400000) + 1)
  );
  const yearFraction = totalDaysInYear > 0 ? yearDaysElapsed / totalDaysInYear : 0;

  return {
    year,
    monthly: { daysElapsed: monthDaysElapsed, totalDays: totalDaysInMonth, fraction: monthFraction },
    annual: { daysElapsed: yearDaysElapsed, totalDays: totalDaysInYear, fraction: yearFraction },
  };
}

function computeForecast(spend, reserve, budget, fraction) {
  if (!fraction || fraction <= 0) return null;
  const projectedSpend = (spend || 0) / fraction;
  const projectedTotal = projectedSpend + (reserve || 0);
  const projectedRemaining = (budget || 0) - projectedTotal;
  return { projectedSpend, projectedTotal, projectedRemaining };
}

function forecastLevel(projectedTotal, budget) {
  if (!budget || budget <= 0) return 'gray';
  if (projectedTotal >= budget) return 'red';
  if (projectedTotal >= WARN_THRESHOLD * budget) return 'yellow';
  return 'green';
}

function StatusTile({ title, subtitle, budget, spend, reserve, explainer, testId }) {
  const status = computeStatus(spend, reserve, budget);
  const styles = STATUS_STYLES[status.level];
  const Icon = styles.icon;
  const total = (spend || 0) + (reserve || 0);
  const remaining = (budget || 0) - total;
  const barPct = Math.min(100, Math.max(0, status.pct));

  return (
    <div
      className={`flex-1 rounded-sm border-2 ${styles.border} ${styles.tileBg} p-5`}
      data-testid={testId}
      data-status={status.level}
    >
      <div className="flex items-start justify-between mb-3 gap-2">
        <div>
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-black uppercase tracking-wide rounded-sm ${styles.pill}`}
        >
          <Icon className="w-3.5 h-3.5" />
          {status.label}
        </span>
      </div>

      <div className="mb-3">
        <div className={`text-2xl md:text-3xl font-black ${styles.text} font-chivo leading-tight`}>
          {remaining >= 0
            ? `${fmtUSD(remaining)} available`
            : `Over by ${fmtUSD(Math.abs(remaining))}`}
        </div>
        <div className="text-xs text-slate-500 mt-1">
          {status.pct.toFixed(1)}% of budget used
        </div>
      </div>

      <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden mb-3 relative">
        <div className={`h-full ${styles.bar} transition-all`} style={{ width: `${barPct}%` }} />
        <div
          className="absolute top-0 bottom-0 border-l border-dashed border-slate-500/70"
          style={{ left: `${WARN_THRESHOLD * 100}%` }}
          title="85% warning threshold"
        />
      </div>

      <div className="grid grid-cols-3 gap-2 text-[10px] font-mono mb-3">
        <div>
          <div className="text-slate-400 uppercase tracking-wider">Approved OT</div>
          <div className="text-slate-900 font-bold text-sm">{fmtUSD(spend)}</div>
        </div>
        <div>
          <div className="text-slate-400 uppercase tracking-wider">Sup. Reserve</div>
          <div className="text-slate-900 font-bold text-sm">{fmtUSD(reserve)}</div>
        </div>
        <div>
          <div className="text-slate-400 uppercase tracking-wider">Budget</div>
          <div className="text-slate-900 font-bold text-sm">{fmtUSD(budget)}</div>
        </div>
      </div>

      {explainer && (
        <p className="text-[11px] text-slate-600 leading-relaxed">{explainer}</p>
      )}
    </div>
  );
}

function FieldInput({ label, value, onChange, type, children }) {
  return (
    <div>
      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">
        {label}
      </label>
      {type === 'select' ? (
        <select
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
        >
          {children}
        </select>
      ) : type === 'date' ? (
        <input
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
        />
      ) : (
        <input
          type="number"
          step="any"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
        />
      )}
    </div>
  );
}

function ForecastRow({ label, subtitle, forecast, budget, period }) {
  if (!forecast) {
    return (
      <div className="p-3 bg-white border border-slate-200 rounded-sm" data-testid={`forecast-${period}`}>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</div>
          <div className="text-[10px] text-slate-400">{subtitle}</div>
        </div>
        <div className="text-xs text-slate-500 italic">
          Not enough of the period has elapsed yet to project.
        </div>
      </div>
    );
  }

  const { projectedTotal, projectedRemaining } = forecast;
  const level = forecastLevel(projectedTotal, budget);
  const styles = STATUS_STYLES[level];

  return (
    <div
      className={`p-3 bg-white border-l-4 ${styles.border} border-y border-r border-slate-200 rounded-sm`}
      data-testid={`forecast-${period}`}
      data-forecast-level={level}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{label}</div>
        <div className="text-[10px] text-slate-400">{subtitle}</div>
      </div>
      <p className="text-xs text-slate-700 leading-relaxed">
        At the current rate of OT usage, you are estimated to use{' '}
        <strong className={styles.text}>{fmtUSD(projectedTotal)}</strong> of the{' '}
        <span className="font-mono">{fmtUSD(budget)}</span> budget,
        {projectedRemaining >= 0 ? (
          <>
            {' '}leaving <strong className={styles.text}>{fmtUSD(projectedRemaining)}</strong> remaining.
          </>
        ) : (
          <>
            {' '}putting you <strong className="text-red-700">{fmtUSD(Math.abs(projectedRemaining))} over</strong> budget.
          </>
        )}
      </p>
    </div>
  );
}

function ForecastPanel({ settings, monthlyReserve, annualReserve, monthLabel }) {
  const elapsed = getElapsed(settings.asOfDate, settings.selectedMonth);

  const monthly = computeForecast(
    Number(settings.monthlySpend) || 0,
    monthlyReserve,
    Number(settings.monthlyBudget) || 0,
    elapsed.monthly.fraction,
  );
  const annual = computeForecast(
    Number(settings.annualSpend) || 0,
    annualReserve,
    Number(settings.annualBudget) || 0,
    elapsed.annual.fraction,
  );

  const asOfLabel = parseAsOf(settings.asOfDate).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });

  return (
    <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-sm" data-testid="budget-forecast">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-slate-600" />
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
            At Current Rate of OT Usage
          </h3>
        </div>
        <span className="text-[10px] text-slate-500 font-mono">As of {asOfLabel}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ForecastRow
          period="month"
          label={`${monthLabel} Forecast`}
          subtitle={`${elapsed.monthly.daysElapsed} of ${elapsed.monthly.totalDays} days elapsed`}
          forecast={monthly}
          budget={Number(settings.monthlyBudget) || 0}
        />
        <ForecastRow
          period="year"
          label={`${elapsed.year} Year-End Forecast`}
          subtitle={`Day ${elapsed.annual.daysElapsed} of ${elapsed.annual.totalDays}`}
          forecast={annual}
          budget={Number(settings.annualBudget) || 0}
        />
      </div>
      <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
        Projection = (approved OT spend ÷ fraction of period elapsed) + supervisor reserve for the full period.
        Useful for spotting overruns before they happen; assumes the current burn rate continues.
      </p>
    </div>
  );
}

export default function BudgetStatus() {
  const [settings, setSettings] = useState(loadSettings());
  const [showEditor, setShowEditor] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [form, setForm] = useState(settings);

  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  const monthlyReserve = Number(settings.supervisorReserveMonthly) || 0;
  const annualReserve = monthlyReserve * 12;
  const monthLabel = MONTH_NAMES[settings.selectedMonth] ?? MONTH_NAMES[new Date().getMonth()];
  const year = new Date().getFullYear();

  const handleEdit = () => {
    setForm(settings);
    setShowEditor((v) => !v);
  };

  const handleSave = () => {
    const cleaned = {
      ...form,
      annualBudget: Number(form.annualBudget) || 0,
      annualSpend: Number(form.annualSpend) || 0,
      monthlyBudget: Number(form.monthlyBudget) || 0,
      monthlySpend: Number(form.monthlySpend) || 0,
      supervisorReserveMonthly: Number(form.supervisorReserveMonthly) || 0,
      selectedMonth: Number(form.selectedMonth),
      asOfDate: form.asOfDate || new Date().toISOString().slice(0, 10),
    };
    setSettings(cleaned);
    setShowEditor(false);
  };

  const handleCancel = () => {
    setForm(settings);
    setShowEditor(false);
  };

  return (
    <div
      className="mb-8 bg-white rounded-sm border border-slate-200 shadow-sm"
      data-testid="budget-status"
    >
      <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-bold tracking-tight text-slate-800 uppercase flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-600" />
          OT Budget Status — {settings.unitLabel}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowInfo((v) => !v)}
            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
            data-testid="budget-info-toggle"
          >
            <Info className="w-3 h-3" /> About
          </button>
          <button
            onClick={handleEdit}
            className="flex items-center gap-1 px-3 py-1.5 bg-slate-900 text-white text-xs font-bold rounded-sm hover:bg-slate-800"
            data-testid="budget-edit-toggle"
          >
            <Edit3 className="w-3 h-3" />
            {showEditor ? 'Close Editor' : 'Update Figures'}
          </button>
        </div>
      </div>

      {showInfo && (
        <div className="px-4 py-3 bg-blue-50 border-b border-blue-200 text-xs text-blue-900 leading-relaxed">
          <strong>How this works:</strong> The indicator shows whether {settings.unitLabel} still has approved overtime budget available.{' '}
          <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-600 text-white font-bold">GREEN</span>{' '}
          means less than 85% of the budget has been used and funds are available.{' '}
          <span className="inline-block px-1.5 py-0.5 rounded bg-amber-500 text-white font-bold">YELLOW</span>{' '}
          means 85%–100% used — approaching the limit.{' '}
          <span className="inline-block px-1.5 py-0.5 rounded bg-red-600 text-white font-bold">RED</span>{' '}
          means the budget has been exceeded. The figure includes approved officer overtime plus a monthly supervisor reserve of {fmtUSD(monthlyReserve)} for supervisor OT typically logged after the fact.
        </div>
      )}

      <div className="p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <StatusTile
            testId="budget-tile-month"
            title="This Month"
            subtitle={`${monthLabel} ${year}`}
            budget={Number(settings.monthlyBudget) || 0}
            spend={Number(settings.monthlySpend) || 0}
            reserve={monthlyReserve}
            explainer={`Includes ${fmtUSD(monthlyReserve)} supervisor reserve for the month.`}
          />
          <StatusTile
            testId="budget-tile-year"
            title="Year to Date"
            subtitle={`${year} Annual Budget`}
            budget={Number(settings.annualBudget) || 0}
            spend={Number(settings.annualSpend) || 0}
            reserve={annualReserve}
            explainer={`Includes ${fmtUSD(annualReserve)} annual supervisor reserve (${fmtUSD(monthlyReserve)}/month × 12).`}
          />
        </div>

        <ForecastPanel
          settings={settings}
          monthlyReserve={monthlyReserve}
          annualReserve={annualReserve}
          monthLabel={monthLabel}
        />

        {showEditor && (
          <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-sm" data-testid="budget-editor">
            <h3 className="text-sm font-bold text-slate-700 uppercase mb-3">Update Budget Figures</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <FieldInput
                label="Current Month"
                type="select"
                value={form.selectedMonth}
                onChange={(v) => setForm({ ...form, selectedMonth: v })}
              >
                {MONTH_NAMES.map((m, i) => (
                  <option key={i} value={i}>{m}</option>
                ))}
              </FieldInput>
              <FieldInput
                label="Monthly Budget ($)"
                value={form.monthlyBudget}
                onChange={(v) => setForm({ ...form, monthlyBudget: v })}
              />
              <FieldInput
                label="Approved Monthly Spend ($)"
                value={form.monthlySpend}
                onChange={(v) => setForm({ ...form, monthlySpend: v })}
              />
              <FieldInput
                label="Annual Budget ($)"
                value={form.annualBudget}
                onChange={(v) => setForm({ ...form, annualBudget: v })}
              />
              <FieldInput
                label="Approved YTD Spend ($)"
                value={form.annualSpend}
                onChange={(v) => setForm({ ...form, annualSpend: v })}
              />
              <FieldInput
                label="Supervisor Reserve / Month ($)"
                value={form.supervisorReserveMonthly}
                onChange={(v) => setForm({ ...form, supervisorReserveMonthly: v })}
              />
              <FieldInput
                label="As-Of Date (data current through)"
                type="date"
                value={form.asOfDate}
                onChange={(v) => setForm({ ...form, asOfDate: v })}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white text-sm font-bold rounded-sm hover:bg-emerald-800"
                data-testid="budget-save"
              >
                <Save className="w-4 h-4" /> Save
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 bg-slate-200 text-slate-700 text-sm font-bold rounded-sm hover:bg-slate-300"
                data-testid="budget-cancel"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
              Pull the latest Approved OT figures from the Tableau dashboard each month and paste them here.
              Values are saved in this browser (localStorage) — if you clear browser data they will reset to the defaults.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
